import { Leg } from './strategy';
import {
  BROKER_NAME, ACCOUNT_STATUS, ACTION_TYPE, ORDER_TAG, ORDER_STATUS,
  EXIT_TYPE, POSITION_STATUS, STATUS, ALLOCATION_STATUS, BASKET_STATUS, STRATEGY_STATUS,
} from '../constants/tradingConstants';

// Derived types from constants
export type BrokerName = typeof BROKER_NAME[keyof typeof BROKER_NAME];
export type AccountStatus = typeof ACCOUNT_STATUS[keyof typeof ACCOUNT_STATUS];
export type OrderSide = typeof ACTION_TYPE[keyof typeof ACTION_TYPE];
export type OrderTagValue = typeof ORDER_TAG[keyof typeof ORDER_TAG];
export type OrderStatusValue = typeof ORDER_STATUS[keyof typeof ORDER_STATUS];
export type ExitTypeValue = typeof EXIT_TYPE[keyof typeof EXIT_TYPE] | null;
export type PositionStatusValue = typeof POSITION_STATUS[keyof typeof POSITION_STATUS];
export type BasketStrategyStatus = typeof STATUS[keyof typeof STATUS];
export type BasketStatusValue = typeof BASKET_STATUS[keyof typeof BASKET_STATUS];
export type StrategyStatusValue = typeof STRATEGY_STATUS[keyof typeof STRATEGY_STATUS];
export type AllocationStatusValue = typeof ALLOCATION_STATUS[keyof typeof ALLOCATION_STATUS];

// User Types
export interface User {
  id: string;
  email: string;
  phoneNumber: string;
  fullName: string;
  state: string;
  createdAt: string;
  groups?: string[];
}

export interface UserRegistration {
  phone_number: string;
  email: string;
  full_name: string;
  state: string;
  password: string;
}

export interface UserLogin {
  username: string;
  password: string;
}

export interface AuthTokens {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  expiresIn: number;
}

// Broker Account Types
export interface BrokerAccount {
  user_id: string;
  client_id: string;
  broker_name: BrokerName;
  group: 'BFW' | 'KOU' | 'PMS';
  capital: number;
  account_status: AccountStatus;
  description: string;
  created_at: string;
  updated_at: string;
  has_credentials: boolean;
  has_oauth_token: boolean;
  is_oauth_valid?: boolean;        // True if token exists and hasn't expired
  requires_reauth?: boolean;       // True if token is expired or missing
  token_expires_at?: string;
  last_oauth_login?: string;
  auto_login_enabled?: boolean;
  // Per-account start time for the per-minute checks (SL/WAT/ORB/RE/TP/TSL/MTM/
  // broker-sync). Absent = default MarketHours.START (09:16). See CHECK_START_GATE.
  check_start_hour?: number;
  check_start_minute?: number;
  metadata?: {
    exchanges_enabled: string[];
    products_enabled: string[];
  };
}

export interface CreateBrokerAccount {
  broker_name: BrokerName;
  client_id: string;
  api_key: string;
  api_secret: string;
  capital: number;
  description?: string;
  auto_login_enabled?: boolean;
  password?: string;
  totp_key?: string;
}

// Admin-managed Zebu account (Phase 2 of admin-managed-zebu-accounts).
// A row in this list represents (admin → managed Zebu account) — the
// managed user's own broker_accounts row stays under their own user_id,
// this is purely a relation enriched with display fields.
export interface ManagedAccount {
  managed_user_id: string;
  managed_client_id: string;
  broker_name: string;
  email: string;
  full_name: string;
  capital: number | null;
  auto_login_enabled: boolean;
  last_oauth_login?: string;
  token_expires_at?: string;
  granted_at?: string;
  scopes: string[];
  /** Phase 2 client-groups: FK to client-groups.group_id (empty string if untagged). */
  group_id?: string;
  /** Resolved name + color from the client-groups lookup (empty if no group). */
  group_name?: string;
  group_color?: string;
}

export interface UpdateBrokerAccount {
  api_key?: string;
  api_secret?: string;
  capital?: number;
  description?: string;
  auto_login_enabled?: boolean;
  password?: string;
  totp_key?: string;
  check_start_hour?: number;
  check_start_minute?: number;
}

// API Response Types
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message: string;
  errors?: string[];
}

// Orders (UI)
export type OrderSideUI = 'BUY_E' | 'SELL_S';

export interface OrdersApiRow {
  basketId: string;
  basketName: string;
  strategyId: string;
  strategyName: string;
  symbol: string;
  exchange: string;
  clientId: string;
  broker: string;
  positionId: string;
  side: OrderSide;
  tag: string;              // raw broker tag: "QE_xxx" or "QS_xxx"
  qty: number;
  price: number;
  product: string;
  time: string;
  status: OrderStatusValue;
  rejectionReason?: string;
  // Broker-native fields (may be empty for Zebu/Finvasia)
  orderId?: string;
  orderType?: string;       // LIMIT, MARKET, SL, SL-M
  variety?: string;         // regular, stop_loss
  triggerPrice?: number;
  filledQty?: number;
  pendingQty?: number;
  statusMessage?: string;
}

export interface OrdersSummary {
  total: number;
  complete: number;
  rejected: number;
  cancelled: number;
  open: number;
}

export interface GroupedOrdersStrategy {
  strategyId: string;
  strategyName: string;
  orders: OrdersApiRow[];
}

export interface GroupedOrdersBasket {
  basketId: string;
  basketName: string;
  strategies: GroupedOrdersStrategy[];
}

export interface OrdersApiResponse {
  orders: OrdersApiRow[];
  baskets: GroupedOrdersBasket[];
  summary: OrdersSummary;
}

// Positions (derived from entry/exit order pairs)
export interface PositionRow {
  basketId: string;
  basketName: string;
  /** True when the strategy is running under a marketplace subscription
   *  (basket owned by an admin/publisher). Mirrors ScheduleRow.is_marketplace;
   *  threaded through by `scheduleToPositionRow`. Drives violet styling and
   *  the MP pill on the Positions view. */
  isMarketplace?: boolean;
  strategyId: string;
  strategyName: string;
  clientId: string;
  product: string;
  positionId: string;
  symbol: string;
  side: OrderSide;
  qty: number;
  /** Number of lots (qty / lot_size). Null when the lot_size can't be derived
   *  (e.g. unknown underlying on a legacy row). */
  lots?: number | null;
  entryPrice: number | null;
  exitPrice: number | null;
  entryTime: string;
  exitTime: string | null;
  exitType: ExitTypeValue;
  ltp: number | null;
  pnl: number | null;
  // null = synthesized schedule placeholder (leg projected from config but no
  // PositionRecord yet). Real positions always carry a concrete status.
  status: PositionStatusValue | null;
  entryStatus: string;
  entryError: string | null;
  /**
   * Per-position missed-exit indicator — populated by the Schedule adapter
   * (`scheduleToPositionRow`) when the backend stamps it on today's view.
   * ABSENT for healthy positions and on Positions-page rows (which don't
   * carry missed-exit detection — those use the dedicated indicators on
   * the Schedule page). Drives the second inline red chip on the position
   * row and modal.
   */
  missedExit?: { reason: 'past_exit' | 'stuck_pending'; stuck_minutes?: number };
  slStatus: string;
  // EXIT_PENDING tracking — set when an exit order is at the broker awaiting fill.
  exitStatus?: string;
  exitAttemptCount?: number;
  exitError?: string | null;
  exitViaSlModify?: boolean;
  tag: string;

  // ── Rich lifecycle fields (Strategy view) ──
  legId?: string;
  legIndex?: number;
  entrySource?: string;
  /**
   * Chain-monotonic spawn depth — 0 for the original entry, 1+ for every
   * subsequent spawn in the chain regardless of feature (SL_REENTRY,
   * SL_RECOST, SL_REEXEC, TP_REEXEC all increment it).
   *
   * Drives `ReSequenceBadge` (the small round chip showing chain depth).
   * NOT a per-feature attempt count — for "how many SL re-entries / TP
   * re-execs have fired so far", use `reEntryCount` / `tpReexecCount` and
   * the `EntrySourceBadge` budget chips, which encode that separately.
   */
  sequenceNo?: number;
  /** Links re-entry / re-cost / re-exec / tp-exec children to their parent. */
  parentPositionId?: string;
  /** Strike-selection method from the leg config (e.g. "PREMIUM", "ATM", "ATM_POINTS"). */
  selectionMethod?: string;
  /** Numeric target for the selection method (e.g. 90 for PREMIUM ≈ 90). */
  selectionValue?: number | null;

  // Entry
  expectedEntryPrice?: number | null;
  averageEntryPrice?: number | null;
  entrySlippage?: number | null;
  entryAttemptCount?: number;
  /** Chase reprice count for entry (>0 means LIMIT was modified during chase). */
  entryRepriceCount?: number;
  entryTriggerPrice?: number | null;
  /** Canonical (user-configured) entry trigger. See `entryCircuitCapped` —
   *  when truthy, the broker forced `entryTriggerPrice` below this canonical
   *  level due to a circuit-cap clamp. Phase 3 recovery retries the canonical
   *  level as bands widen. */
  entryDesiredTriggerPrice?: number | null;
  /** Toggles the "capped" chip on the Entry cell of `StrategyPositionsModal`. */
  entryCircuitCapped?: boolean;
  entryFilledAt?: string | null;

  // Stop-loss
  slTriggerPrice?: number | null;
  /** Canonical SL trigger — see `slCircuitCapped`. Phase 2 recovery retries
   *  the canonical level as bands widen across days. */
  slDesiredTriggerPrice?: number | null;
  /** Toggles the "capped" chip on the Stop-Loss cell. */
  slCircuitCapped?: boolean;
  slLimitPrice?: number | null;
  slAveragePrice?: number | null;
  slAttemptCount?: number;
  /** Chase reprice count for SL (>0 means SL LIMIT was modified during chase). */
  slRepriceCount?: number;
  slError?: string | null;
  slPlacedAt?: string | null;
  slUpdatedAt?: string | null;
  slOrderId?: string;
  slConfig?: { type: string; value: number; reinstate_overnight?: boolean } | null;

  // Exit
  exitAveragePrice?: number | null;
  expectedExitPrice?: number | null;
  exitLimitPrice?: number | null;
  exitPlacedAt?: string | null;
  exitSlippage?: number | null;

  // WAT / ORB — WAT config now carries UL spot fields for UL W&T rows.
  // `trigger` remains the premium trigger (0 on UL rows since UL W&T rests
  // no broker order); `spotTrigger` + `referenceSpot` + `tradeOnlyFirstEntry`
  // are populated when the config's `type` is a UL W&T variant (see
  // constants.UL_WAT_TYPES). Mirrors `ScheduleRow.wait_and_trade_config`.
  waitAndTradeConfig?: {
    type: string;
    value: number;
    trigger: number;
    candleClose?: number;
    offset?: number;
    spotTrigger?: number;
    referenceSpot?: number;
    tradeOnlyFirstEntry?: boolean;
  } | null;
  orbConfig?: { rangeHigh: number; rangeLow: number; rangeStart: string; rangeEnd: string; breakoutSide: string } | null;

  // Associate-hedge — this position is a protective BUY hedge leg (is_hedge on
  // the leg config); drives the HEDGE indicator in the positions views.
  isHedge?: boolean;

  // ── Phase 1 — feature configs (nullable; absent on legacy rows) ────────────
  targetProfitConfig?: { enabled: boolean; type: string; value: number } | null;
  trailingSlConfig?: { enabled: boolean; type: string; instrumentMoveValue: number; stopLossMoveValue: number } | null;
  reEntryConfig?: { enabled: boolean; type: string; count: number } | null;
  reExecuteConfig?: { enabled: boolean; type: string; count: number } | null;
  moveSlToCostConfig?: { enabled: boolean } | null;
  // ── Phase 1 — feature state ────────────────────────────────────────────────
  reEntryCount?: number;
  tpReexecCount?: number;
  reEntrySpawned?: boolean;
  targetProfitSpawned?: boolean;
  reEntryGateArmed?: boolean;
  tslInitialSlTrigger?: number | null;
  tslExtremePrice?: number | null;
  tslModifyCount?: number;
  slMovedToCost?: boolean;
  pendingExitType?: string | null;
  rootExpectedEntryPrice?: number | null;
  positionExpiryType?: string | null;
  positionExpiryDate?: string | null;
  // ── UL (underlying-based) projected fields (Phase 5e.H5 + Phase 6 review-fix H4)
  // Populated by `scheduleToPositionRow` from the same-named fields on
  // ScheduleRow (which comes from backend `schedule_viewer._overlay_position`,
  // lines 1827-1836). Non-zero only when the leg carries a UL config.
  // Position-detail modals surface these spot anchors alongside premium
  // trigger/level fields.
  underlyingReferencePrice?: number | null;
  underlyingEntryTriggerPrice?: number | null;
  underlyingEntryPrice?: number | null;
  underlyingExitPrice?: number | null;
  underlyingSlLevel?: number | null;
  underlyingTargetLevel?: number | null;
  ulTslInitialSlLevel?: number | null;
  ulTslExtremeSpot?: number | null;
  ulTslModifyCount?: number;
  /** UL W&T leg-config flag mirrored onto the position row. */
  tradeOnlyFirstEntry?: boolean;

  /**
   * Populated only when this row comes from the Schedule page. Tells the
   * modal why the row is on the viewed day: entered/closed/carried.
   * Never set on Positions-page callers.
   */
  source?: 'entered' | 'closed' | 'carried' | null;

  /**
   * Stable per-firing allocation id (`exec_<uuid>` or `legacy_<strategy>_<date>`).
   * When the strategy modal sees 2+ distinct `executionId`s — common for
   * STBT/BTST on the exit day, where yesterday's run is still closing and
   * today's run has just entered — rows are grouped by it so trader sees one
   * section per strategy run. Populated by the Schedule adapter; left
   * undefined by the Positions page (modal renders flat in that case).
   */
  executionId?: string;
  /** Date the position fired ("YYYY-MM-DD"). Group label uses it to show
   *  whether the run entered today or on a prior day. */
  executionDate?: string | null;
  /** Date the position closed ("YYYY-MM-DD") — set once the exit fills.
   *  Used for the group label on closed-today runs. */
  closedDate?: string | null;
}

// Email Verification Response Type
export interface EmailVerificationResponse {
  message: string;
  verified: boolean;
  account_status: string;
  email_verified?: boolean;
}

// Auth Context Types
export interface AuthContextType {
  user: User | null;
  tokens: AuthTokens | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (credentials: UserLogin) => Promise<void>;
  register: (userData: UserRegistration) => Promise<ApiResponse<any>>;
  logout: () => void;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (email: string, code: string, newPassword: string) => Promise<void>;
  verifyEmail: (email: string, confirmationCode: string) => Promise<void>;
  resendVerification: (email: string, verificationType: 'email' | 'phone') => Promise<void>;
  clearError: () => void;
}

// Indian States
export const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
  'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
  'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana',
  'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Andaman and Nicobar Islands', 'Chandigarh', 'Dadra and Nagar Haveli',
  'Daman and Diu', 'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Lakshadweep', 'Puducherry'
] as const;

export type IndianState = typeof INDIAN_STATES[number];

// Basket and Strategy Types for Options Platform
export interface StrategyMetadata {
  strategyId: string;
  strategyName: string;
  strategyType: string;
  status: StrategyStatusValue;
  legCount: number; // Clean unified field
  /** Largest leg.lots in this strategy. Drives the lot_multiplier ceiling at
   *  allocation time: lot_multiplier × maxLegLots ≤ MAX_EFFECTIVE_LOTS_PER_LEG. */
  maxLegLots?: number;
  createdAt?: string; // ISO timestamp — used for stable display ordering
  displayOrder?: number; // User-defined sort position within a basket (null = legacy, sinks to bottom)

  // Configuration fields for display
  underlying?: string;
  expiryType?: string;
  product?: string;
  tradingType?: string;
  intradayExitMode?: string;
  entryTradingDaysBeforeExpiry?: number;
  exitTradingDaysBeforeExpiry?: number;
  description?: string;

  // Display fields
  entryTime?: string;
  exitTime?: string;
  entryDays?: string[];
  exitDays?: string[];
  /** INTRADAY entry-selection mode — undefined behaves as "WEEKDAY". */
  entrySelectionMode?: 'WEEKDAY' | 'DTE';
  /** DTE chips picked when entrySelectionMode === "DTE". */
  entryDtes?: number[];

  // Risk management display
  targetProfit?: { enabled?: boolean; type?: string; value?: number; };
  stopLoss?: { enabled?: boolean; type?: string; value?: number; };
  moveSlToCost?: boolean;
  rangeBreakout?: boolean;
  /** Range-breakout end time "HH:MM" — present only when rangeBreakout is on. */
  rangeBreakoutTime?: string;
  /** Carry forward unfilled ORB SL-trigger entries to next trading day (NRML only). */
  orbContinueOvernight?: boolean;
  /** True when at least one leg carries a UL (underlying-based) SL/TP/TSL/W&T
   *  config. Derived on the frontend from raw legs in `transformStrategyFields`
   *  so the Baskets-page feature chip doesn't need a new backend field. */
  hasUl?: boolean;
}

export interface StrategyWithLegs extends StrategyMetadata {
  legs: Leg[]; // Import Leg from types/strategy.ts
  sequenceNumber?: number;
  isIntraDay?: boolean;
}

/** @deprecated Use StrategyMetadata for display or StrategyWithLegs for editing */
export interface Strategy extends StrategyMetadata {
  /** @deprecated Use legCount instead */
  legs: number;
  /** @deprecated Use StrategyWithLegs.legs instead */
  legsArray?: any[];
}

export interface Basket {
  basket_id: string;
  basket_name: string;
  description?: string;
  strategies: StrategyMetadata[];
  status: BasketStatusValue;
  created_at: string;
  updated_at?: string;
  initial_capital?: number;
  display_order?: number; // User-defined sort position (null = legacy, sinks to bottom)
  // Owner-only reference link — surfaced only on owner-facing surfaces.
  // Never exposed to marketplace subscribers or any consumer-facing API.
  stockmock_url?: string;
}

export interface CreateBasket {
  basket_name: string;
  description?: string;
  strategies: string[];
  initial_capital: number;
  stockmock_url?: string;
}

export interface UpdateBasket {
  basket_name?: string;
  description?: string;
  strategies?: string[];
  status?: BasketStatusValue;
  initial_capital?: number;
  // Explicit empty string clears the stored URL.
  stockmock_url?: string;
}

// Basket-Broker Allocation Types (New Separate System)
export interface BasketBrokerAllocation {
  allocation_id: string;
  basket_id: string;
  broker_id: string;
  client_id: string;
  lot_multiplier: number;
  status: AllocationStatusValue;
  created_at: string;
  updated_at: string;

  // Enhanced fields for global allocations view
  basket_name?: string;
  basket_status?: string;
  strategies_count?: number;
  broker_name?: string;

  // `MARKETPLACE` when this allocation is a marketplace subscription (basket
  // owned by an admin/publisher); unset/empty for user-owned baskets.
  source?: string;

  // Additional fields from API
  priority?: number;
  max_lots_per_order?: number;
  risk_limit_per_trade?: number;
  max_daily_trades?: number;
  stop_loss_percentage?: number;
  total_executions?: number;
  successful_executions?: number;
  total_pnl?: number;
  avg_execution_time_ms?: number;
  last_execution_date?: string | null;
  entity_type?: string;
  entity_type_priority?: string;
  version?: number;
  sort_key?: string;
  user_id?: string;
}

export interface CreateAllocation {
  allocations: {
    broker_id: string;
    client_id: string;
    lot_multiplier: number;
  }[];
}

export interface UpdateAllocation {
  lot_multiplier: number;
  status?: AllocationStatusValue;
}